import { CapabilityDomain, ExtendedDomainAssessment } from './types';

// =============================================================================
// EXTENDED SKILLS QUESTIONS PER DOMAIN
// =============================================================================
//
// Voor elk van de 7 domeinen: 15-20 concrete vragen over:
// - Huidige Situatie (wat doe je nu?)
// - Concrete Vaardigheden (wat kun je?)
// - Knelpunten (waar loop je tegenaan?)
// - Actieplan (wat ga je doen?)
//
// =============================================================================

export const EXTENDED_SKILLS_QUESTIONS: Record<CapabilityDomain, ExtendedDomainAssessment> = {
  
  // ===========================================================================
  // 1. STRATEGIE & RICHTING
  // ===========================================================================
  strategy: {
    domain: 'strategy',
    sections: [
      {
        title: 'Huidige Situatie',
        description: 'Laat ons weten waar je nu staat met je strategie en planning',
        questions: [
          {
            id: 'has_plan',
            type: 'select',
            question: 'Heb je een businessplan of strategie?',
            options: ['Ja, uitgewerkt op papier', 'Ja, maar vooral in mijn hoofd', 'Deels, nog niet compleet', 'Nee, nog niet']
          },
          {
            id: 'clarity',
            type: 'scale',
            question: 'Hoe helder is je langetermijn visie?',
            scale: ['Heel onduidelijk', 'Beetje helder', 'Redelijk helder', 'Heel helder', 'Glashelder']
          },
          {
            id: 'time_investment',
            type: 'select',
            question: 'Hoeveel tijd besteed je aan strategie en planning?',
            options: ['Bijna nooit', 'Minder dan 2 uur per maand', '2-5 uur per maand', '5-10 uur per maand', 'Meer dan 10 uur per maand']
          }
        ]
      },
      {
        title: 'Concrete Vaardigheden',
        description: 'Beoordeel je strategische vaardigheden eerlijk',
        questions: [
          {
            id: 'skill_vision',
            type: 'scale',
            question: 'Kun je een heldere visie formuleren en communiceren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_goals',
            type: 'scale',
            question: 'Kun je realistische doelen stellen?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_prioritize',
            type: 'scale',
            question: 'Kun je prioriteiten bepalen en focus houden?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_pivot',
            type: 'scale',
            question: 'Kun je bijsturen als iets niet werkt?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          }
        ]
      },
      {
        title: 'Knelpunten',
        description: 'Wat zijn je grootste uitdagingen op strategisch gebied?',
        questions: [
          {
            id: 'biggest_challenge',
            type: 'textarea',
            question: 'Wat is het grootste strategische knelpunt?',
            placeholder: 'Bijvoorbeeld: "Ik weet niet waar ik mijn focus moet leggen"'
          },
          {
            id: 'missing_knowledge',
            type: 'multiselect',
            question: 'Welke strategische kennis mis je?',
            options: [
              'Businessplan maken',
              'Marktonderzoek doen',
              'Concurrentieanalyse',
              'Financiële planning',
              'Groeistrategie bepalen',
              'Risico\'s inschatten',
              'Pivot strategie'
            ]
          }
        ]
      },
      {
        title: 'Actieplan',
        description: 'Hoe ga je dit oplossen?',
        questions: [
          {
            id: 'first_action',
            type: 'select',
            question: 'Wat moet er als eerste gebeuren?',
            options: [
              'Ik ga dit zelf uitzoeken (cursus/boek)',
              'Ik neem een business coach',
              'Ik vraag advies aan mentor/netwerk',
              'Ik huur een strategisch adviseur in',
              'Nog onbekend'
            ]
          },
          {
            id: 'timeline',
            type: 'select',
            question: 'Binnen welke termijn moet dit opgelost zijn?',
            options: ['Deze maand', 'Binnen 3 maanden', 'Binnen 6 maanden', 'Dit jaar', 'Geen haast']
          }
        ]
      }
    ]
  },

  // ===========================================================================
  // 2. MARKETING & ZICHTBAARHEID
  // ===========================================================================
  marketing: {
    domain: 'marketing',
    sections: [
      {
        title: 'Huidige Situatie',
        questions: [
          {
            id: 'current_activities',
            type: 'multiselect',
            question: 'Wat doe je nu al op marketinggebied?',
            options: [
              'Social media posts',
              'Email marketing',
              'Website/blog content',
              'Betaalde advertenties (ads)',
              'Netwerken/events',
              'SEO optimalisatie',
              'Influencer marketing',
              'Nog niets'
            ]
          },
          {
            id: 'time_investment',
            type: 'select',
            question: 'Hoeveel tijd besteed je aan marketing per week?',
            options: ['Minder dan 2 uur', '2-5 uur', '5-10 uur', '10-20 uur', 'Meer dan 20 uur']
          },
          {
            id: 'tools_used',
            type: 'text',
            question: 'Welke marketing tools gebruik je?',
            placeholder: 'Bijvoorbeeld: Canva, Mailchimp, Google Ads, Meta Business Suite...'
          },
          {
            id: 'budget',
            type: 'select',
            question: 'Hoeveel budget heb je voor marketing per maand?',
            options: ['€0 (alleen tijd)', '€1-100', '€100-500', '€500-1000', 'Meer dan €1000']
          }
        ]
      },
      {
        title: 'Concrete Vaardigheden',
        questions: [
          {
            id: 'skill_target_audience',
            type: 'scale',
            question: 'Kun je een heldere doelgroep definiëren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_content',
            type: 'scale',
            question: 'Kun je content maken (tekst/beeld/video)?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_paid_ads',
            type: 'scale',
            question: 'Kun je betaalde advertenties opzetten en optimaliseren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_analytics',
            type: 'scale',
            question: 'Kun je marketingresultaten meten en analyseren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_copywriting',
            type: 'scale',
            question: 'Kun je overtuigende teksten schrijven?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          }
        ]
      },
      {
        title: 'Knelpunten',
        questions: [
          {
            id: 'biggest_challenge',
            type: 'textarea',
            question: 'Wat is het grootste marketing knelpunt?',
            placeholder: 'Bijvoorbeeld: "Ik weet niet hoe ik mijn doelgroep moet bereiken"'
          },
          {
            id: 'missing_knowledge',
            type: 'multiselect',
            question: 'Welke marketing kennis mis je?',
            options: [
              'Doelgroep bepalen',
              'Content strategie',
              'Online adverteren (Google/Meta)',
              'Social media algoritmes',
              'Email marketing opzetten',
              'Conversie optimalisatie',
              'SEO (zoekmachine optimalisatie)',
              'Data analyse en metrics'
            ]
          }
        ]
      },
      {
        title: 'Actieplan',
        questions: [
          {
            id: 'first_action',
            type: 'select',
            question: 'Wat moet er als eerste gebeuren?',
            options: [
              'Ik ga dit zelf leren (online cursus)',
              'Ik neem een marketing expert/consultant',
              'Ik besteed dit uit aan een marketing bureau',
              'Ik vraag iemand uit mijn netwerk om hulp',
              'Nog onbekend'
            ]
          },
          {
            id: 'timeline',
            type: 'select',
            question: 'Binnen welke termijn moet dit marketing draaien?',
            options: ['Deze maand', 'Binnen 3 maanden', 'Binnen 6 maanden', 'Dit jaar', 'Geen haast']
          }
        ]
      }
    ]
  },

  // ===========================================================================
  // 3. SALES & CONVERSIE
  // ===========================================================================
  sales: {
    domain: 'sales',
    sections: [
      {
        title: 'Huidige Situatie',
        questions: [
          {
            id: 'current_process',
            type: 'select',
            question: 'Heb je een verkoopproces?',
            options: ['Ja, uitgewerkt en gedocumenteerd', 'Ja, maar ad-hoc en inconsistent', 'Deels, nog in ontwikkeling', 'Nee, nog niet']
          },
          {
            id: 'leads_per_month',
            type: 'select',
            question: 'Hoeveel leads krijg je gemiddeld per maand?',
            options: ['0', '1-5', '5-10', '10-25', '25-50', 'Meer dan 50']
          },
          {
            id: 'conversion_rate',
            type: 'select',
            question: 'Wat is je geschatte conversie (lead → klant)?',
            options: ['Weet ik niet', 'Minder dan 10%', '10-25%', '25-50%', 'Meer dan 50%']
          },
          {
            id: 'sales_cycle',
            type: 'select',
            question: 'Hoe lang duurt je gemiddelde sales cycle?',
            options: ['Direct (< 1 dag)', '1-7 dagen', '1-4 weken', '1-3 maanden', 'Langer dan 3 maanden']
          }
        ]
      },
      {
        title: 'Concrete Vaardigheden',
        questions: [
          {
            id: 'skill_pitch',
            type: 'scale',
            question: 'Kun je een heldere pitch geven?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_objections',
            type: 'scale',
            question: 'Kun je omgaan met bezwaren en twijfels?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_closing',
            type: 'scale',
            question: 'Kun je deals sluiten en vragen om commitment?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_follow_up',
            type: 'scale',
            question: 'Kun je leads goed opvolgen en bijhouden?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_negotiation',
            type: 'scale',
            question: 'Kun je onderhandelen over prijs en voorwaarden?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          }
        ]
      },
      {
        title: 'Knelpunten',
        questions: [
          {
            id: 'biggest_challenge',
            type: 'textarea',
            question: 'Wat is het grootste sales knelpunt?',
            placeholder: 'Bijvoorbeeld: "Ik krijg geen leads binnen" of "Ik durf niet te vragen om de deal"'
          },
          {
            id: 'missing_knowledge',
            type: 'multiselect',
            question: 'Welke sales kennis mis je?',
            options: [
              'Lead generatie',
              'Sales funnel opzetten',
              'Pitchen en presenteren',
              'Onderhandelen',
              'CRM systeem gebruiken',
              'Follow-up systematiek',
              'Closing technieken',
              'Objectie-handling'
            ]
          }
        ]
      },
      {
        title: 'Actieplan',
        questions: [
          {
            id: 'first_action',
            type: 'select',
            question: 'Wat moet er als eerste gebeuren?',
            options: [
              'Ik ga sales training volgen',
              'Ik neem een sales coach',
              'Ik huur een verkoper in',
              'Ik automatiseer mijn sales funnel',
              'Nog onbekend'
            ]
          },
          {
            id: 'timeline',
            type: 'select',
            question: 'Binnen welke termijn moet sales draaien?',
            options: ['Deze maand', 'Binnen 3 maanden', 'Binnen 6 maanden', 'Dit jaar', 'Geen haast']
          }
        ]
      }
    ]
  },

  // ===========================================================================
  // 4. PRODUCT / DIENST
  // ===========================================================================
  product: {
    domain: 'product',
    sections: [
      {
        title: 'Huidige Situatie',
        questions: [
          {
            id: 'product_status',
            type: 'select',
            question: 'Wat is de status van je product/dienst?',
            options: [
              'Nog een idee',
              'In ontwikkeling',
              'MVP (Minimum Viable Product) klaar',
              'Volledig ontwikkeld',
              'Al verkocht aan klanten'
            ]
          },
          {
            id: 'customer_feedback',
            type: 'select',
            question: 'Heb je klantfeedback verzameld?',
            options: ['Ja, veel feedback', 'Ja, wat feedback', 'Nee, nog niet', 'Niet nodig (nog geen klanten)']
          },
          {
            id: 'iteration_speed',
            type: 'select',
            question: 'Hoe snel kun je je product/dienst aanpassen?',
            options: ['Binnen dagen', 'Binnen weken', 'Binnen maanden', 'Moeilijk aan te passen']
          }
        ]
      },
      {
        title: 'Concrete Vaardigheden',
        questions: [
          {
            id: 'skill_development',
            type: 'scale',
            question: 'Kun je het product/dienst zelf ontwikkelen?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_design',
            type: 'scale',
            question: 'Kun je het product/dienst ontwerpen (UX/UI)?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_quality',
            type: 'scale',
            question: 'Kun je kwaliteit waarborgen en testen?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_iterate',
            type: 'scale',
            question: 'Kun je itereren op basis van feedback?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          }
        ]
      },
      {
        title: 'Knelpunten',
        questions: [
          {
            id: 'biggest_challenge',
            type: 'textarea',
            question: 'Wat is het grootste product knelpunt?',
            placeholder: 'Bijvoorbeeld: "Ik weet niet of mijn product goed genoeg is"'
          },
          {
            id: 'missing_knowledge',
            type: 'multiselect',
            question: 'Welke product kennis mis je?',
            options: [
              'Product development',
              'Design thinking',
              'User experience (UX)',
              'Kwaliteitscontrole',
              'Technische kennis',
              'Productstrategie',
              'MVP bouwen',
              'Iteratie proces'
            ]
          }
        ]
      },
      {
        title: 'Actieplan',
        questions: [
          {
            id: 'first_action',
            type: 'select',
            question: 'Wat moet er als eerste gebeuren?',
            options: [
              'Ik ga dit zelf bouwen/maken',
              'Ik neem een ontwikkelaar/maker aan',
              'Ik besteed ontwikkeling uit',
              'Ik koop een bestaande oplossing',
              'Nog onbekend'
            ]
          },
          {
            id: 'timeline',
            type: 'select',
            question: 'Binnen welke termijn moet het product klaar zijn?',
            options: ['Deze maand', 'Binnen 3 maanden', 'Binnen 6 maanden', 'Dit jaar', 'Nog onbekend']
          }
        ]
      }
    ]
  },

  // ===========================================================================
  // 5. OPERATIE & SYSTEMEN
  // ===========================================================================
  operations: {
    domain: 'operations',
    sections: [
      {
        title: 'Huidige Situatie',
        questions: [
          {
            id: 'current_systems',
            type: 'multiselect',
            question: 'Welke systemen gebruik je nu?',
            options: [
              'CRM (klantbeheer)',
              'Boekhoudsoftware',
              'Projectmanagement tool',
              'Communicatie platform (Slack/Teams)',
              'Planning/agenda systeem',
              'Facturatiesoftware',
              'Nog geen systemen'
            ]
          },
          {
            id: 'automation_level',
            type: 'select',
            question: 'Hoeveel is er geautomatiseerd?',
            options: ['Bijna niets', 'Enkele processen', 'Veel processen', 'Bijna alles', 'Volledig geautomatiseerd']
          },
          {
            id: 'manual_work',
            type: 'select',
            question: 'Hoeveel tijd gaat verloren aan handmatig werk per week?',
            options: ['Minder dan 5 uur', '5-10 uur', '10-20 uur', '20-40 uur', 'Meer dan 40 uur']
          }
        ]
      },
      {
        title: 'Concrete Vaardigheden',
        questions: [
          {
            id: 'skill_process',
            type: 'scale',
            question: 'Kun je processen in kaart brengen en documenteren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_automation',
            type: 'scale',
            question: 'Kun je taken automatiseren (met tools/software)?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_systems',
            type: 'scale',
            question: 'Kun je systemen opzetten en beheren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_optimize',
            type: 'scale',
            question: 'Kun je processen optimaliseren en verbeteren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          }
        ]
      },
      {
        title: 'Knelpunten',
        questions: [
          {
            id: 'biggest_challenge',
            type: 'textarea',
            question: 'Wat is het grootste operationele knelpunt?',
            placeholder: 'Bijvoorbeeld: "Ik doe alles nog handmatig, kost veel tijd"'
          },
          {
            id: 'missing_knowledge',
            type: 'multiselect',
            question: 'Welke operationele kennis mis je?',
            options: [
              'Procesoptimalisatie',
              'Automatisering tools (Zapier/Make)',
              'Workflow design',
              'Systeem integratie',
              'Data management',
              'Kwaliteitsborging',
              'Schaalbaarheid'
            ]
          }
        ]
      },
      {
        title: 'Actieplan',
        questions: [
          {
            id: 'first_action',
            type: 'select',
            question: 'Wat moet er als eerste gebeuren?',
            options: [
              'Ik ga processen in kaart brengen',
              'Ik ga automatisering opzetten',
              'Ik neem een operations manager aan',
              'Ik huur een consultant in',
              'Nog onbekend'
            ]
          },
          {
            id: 'timeline',
            type: 'select',
            question: 'Binnen welke termijn moet dit opgelost zijn?',
            options: ['Deze maand', 'Binnen 3 maanden', 'Binnen 6 maanden', 'Dit jaar', 'Geen haast']
          }
        ]
      }
    ]
  },

  // ===========================================================================
  // 6. FINANCIËN
  // ===========================================================================
  finance: {
    domain: 'finance',
    sections: [
      {
        title: 'Huidige Situatie',
        questions: [
          {
            id: 'bookkeeping',
            type: 'select',
            question: 'Hoe regel je je administratie?',
            options: [
              'Boekhouder doet het',
              'Ik doe het zelf',
              'Boekhoudprogramma (automatisch)',
              'Excel spreadsheets',
              'Nog niets opgezet',
              'Chaotisch/ad-hoc'
            ]
          },
          {
            id: 'financial_insight',
            type: 'select',
            question: 'Heb je inzicht in je financiële cijfers?',
            options: ['Ja, dagelijks', 'Ja, wekelijks', 'Ja, maandelijks', 'Soms, onregelmatig', 'Nee, geen idee']
          },
          {
            id: 'runway',
            type: 'select',
            question: 'Hoelang kun je doorgaan zonder omzet (buffer)?',
            options: ['Minder dan 1 maand', '1-3 maanden', '3-6 maanden', '6-12 maanden', 'Meer dan 1 jaar', 'Weet ik niet']
          },
          {
            id: 'profit_loss',
            type: 'select',
            question: 'Draai je nu winst of verlies?',
            options: ['Winst', 'Break-even', 'Verlies', 'Nog geen omzet', 'Weet ik niet precies']
          }
        ]
      },
      {
        title: 'Concrete Vaardigheden',
        questions: [
          {
            id: 'skill_cashflow',
            type: 'scale',
            question: 'Kun je je cashflow beheren en voorspellen?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_pricing',
            type: 'scale',
            question: 'Kun je je pricing strategisch bepalen?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_forecast',
            type: 'scale',
            question: 'Kun je financiële prognoses maken?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_bookkeeping',
            type: 'scale',
            question: 'Kun je je administratie bijhouden?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          }
        ]
      },
      {
        title: 'Knelpunten',
        questions: [
          {
            id: 'biggest_challenge',
            type: 'textarea',
            question: 'Wat is het grootste financiële knelpunt?',
            placeholder: 'Bijvoorbeeld: "Ik heb geen buffer en weet niet waar ik sta"'
          },
          {
            id: 'missing_knowledge',
            type: 'multiselect',
            question: 'Welke financiële kennis mis je?',
            options: [
              'Boekhouding',
              'BTW en belastingen',
              'Cashflow management',
              'Pricing strategie',
              'Financiële planning',
              'Kostprijsberekening',
              'Financiering regelen',
              'Budget maken'
            ]
          }
        ]
      },
      {
        title: 'Actieplan',
        questions: [
          {
            id: 'first_action',
            type: 'select',
            question: 'Wat moet er als eerste gebeuren?',
            options: [
              'Ik ga dit zelf leren (basis boekhouden)',
              'Ik neem een boekhouder',
              'Ik schakel een financial advisor in',
              'Ik gebruik boekhoudprogramma',
              'Nog onbekend'
            ]
          },
          {
            id: 'timeline',
            type: 'select',
            question: 'Binnen welke termijn moet dit opgelost zijn?',
            options: ['Direct (urgent)', 'Binnen 1 maand', 'Binnen 3 maanden', 'Dit jaar', 'Geen haast']
          }
        ]
      }
    ]
  },

  // ===========================================================================
  // 7. TEAM & CAPACITEIT
  // ===========================================================================
  team: {
    domain: 'team',
    sections: [
      {
        title: 'Huidige Situatie',
        questions: [
          {
            id: 'team_size',
            type: 'select',
            question: 'Hoeveel mensen werken er mee aan je business?',
            options: ['Alleen ik (solopreneur)', '2-3 mensen', '4-10 mensen', '11-25 mensen', 'Meer dan 25 mensen']
          },
          {
            id: 'team_type',
            type: 'multiselect',
            question: 'Wie werken er mee?',
            options: [
              'Geen team (solo)',
              'Freelancers',
              'Parttime medewerkers',
              'Fulltime medewerkers',
              'Co-founder(s)',
              'Vrijwilligers/stagiairs'
            ]
          },
          {
            id: 'delegation_level',
            type: 'select',
            question: 'Hoeveel delegeer je?',
            options: [
              'Ik doe alles zelf',
              'Weinig, alleen kleine taken',
              'Redelijk wat',
              'Veel, ik focus op strategie',
              'Bijna alles, ik ben alleen CEO'
            ]
          }
        ]
      },
      {
        title: 'Concrete Vaardigheden',
        questions: [
          {
            id: 'skill_recruit',
            type: 'scale',
            question: 'Kun je mensen aannemen en selecteren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_delegate',
            type: 'scale',
            question: 'Kun je taken effectief delegeren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_manage',
            type: 'scale',
            question: 'Kun je een team aansturen en managen?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          },
          {
            id: 'skill_culture',
            type: 'scale',
            question: 'Kun je een goede teamcultuur creëren?',
            scale: ['Niet', 'Beetje', 'Redelijk', 'Goed', 'Expert']
          }
        ]
      },
      {
        title: 'Knelpunten',
        questions: [
          {
            id: 'biggest_challenge',
            type: 'textarea',
            question: 'Wat is het grootste team knelpunt?',
            placeholder: 'Bijvoorbeeld: "Ik doe alles zelf en raak overbelast"'
          },
          {
            id: 'missing_knowledge',
            type: 'multiselect',
            question: 'Welke team kennis mis je?',
            options: [
              'Recruitment en selectie',
              'Onboarding proces',
              'Delegeren',
              'Feedback geven',
              'Teamdynamiek',
              'Conflicthantering',
              'Performance management',
              'Remote team managen'
            ]
          }
        ]
      },
      {
        title: 'Actieplan',
        questions: [
          {
            id: 'first_action',
            type: 'select',
            question: 'Wat moet er als eerste gebeuren?',
            options: [
              'Ik ga leren delegeren',
              'Ik neem mijn eerste medewerker aan',
              'Ik ga met freelancers werken',
              'Ik zoek een co-founder',
              'Nog onbekend'
            ]
          },
          {
            id: 'timeline',
            type: 'select',
            question: 'Binnen welke termijn moet dit opgelost zijn?',
            options: ['Deze maand', 'Binnen 3 maanden', 'Binnen 6 maanden', 'Dit jaar', 'Geen haast']
          },
          {
            id: 'budget',
            type: 'select',
            question: 'Heb je budget om team uit te breiden?',
            options: [
              'Ja, fulltime salaris mogelijk',
              'Ja, parttime/freelance mogelijk',
              'Nee, eerst omzet nodig',
              'Nog onbekend'
            ]
          }
        ]
      }
    ]
  }
};
